/**
 * The one store of frame data (title, content, status, metadata, card, task
 * ticket), keyed by id in server order. Canvas geometry, selection and
 * presentation stay in the canvas store, whose React Flow nodes follow this
 * store (`stores/canvas-frame-nodes.ts`). Server frames enter through
 * `lib/frame-ingest.ts`.
 */
import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";
import {
  mergeFrameData,
  type FrameData,
  type FrameDataPatch,
} from "@/lib/frame-data";

export type FramePatchEntry = readonly [id: string, patch: FrameDataPatch];

export interface FramesState {
  framesById: Readonly<Record<string, FrameData>>;
  /** Server order: `created_at`, then id. */
  frameOrder: readonly string[];
  /**
   * Bumped whenever every frame is replaced (`replaceFrames`, `clear`, an
   * owner's replace or snapshot reaching a mirror). Wall feeds remember the
   * generation they were built on and start over when it changes.
   */
  generation: number;
  /** Replace every frame, keeping the given order. */
  replaceFrames: (frames: readonly FrameData[]) => void;
  /** Insert a new frame in server order, or replace a known one in place. */
  upsertFrame: (frame: FrameData) => void;
  /** `upsertFrame` for many frames in one write. */
  upsertFrames: (frames: readonly FrameData[]) => void;
  patchFrame: (id: string, patch: FrameDataPatch) => void;
  /** Apply many patches in one write; unknown ids and no-ops are skipped. */
  patchFrames: (entries: readonly FramePatchEntry[]) => void;
  removeFrames: (ids: Iterable<string>) => void;
  /** Drop every frame, or every frame but those `keep` accepts. */
  clear: (keep?: (frame: FrameData) => boolean) => void;
}

function comesBefore(left: FrameData, right: FrameData): boolean {
  if (left.createdAt !== right.createdAt) {
    return left.createdAt < right.createdAt;
  }
  return left.frameId < right.frameId;
}

function insertionIndex(
  order: readonly string[],
  framesById: Readonly<Record<string, FrameData>>,
  frame: FrameData,
): number {
  for (let index = order.length - 1; index >= 0; index -= 1) {
    const existing = framesById[order[index]!];
    if (!existing || !comesBefore(frame, existing)) return index + 1;
  }
  return 0;
}

/**
 * The frames store between windows. A replace (and a snapshot) carries every
 * frame, and the receiver bumps its own generation; any other write carries
 * only the frames it added or changed and the ids it removed, which the
 * receiver merges by id.
 */
export type FramesMirrorPatch =
  | {
      replace: true;
      framesById: Readonly<Record<string, FrameData>>;
      frameOrder: readonly string[];
    }
  | { upserted?: Record<string, FrameData>; removed?: string[] };

/**
 * What one write tells the other windows. Never the whole store for a
 * partial write: a mirror's copy would overwrite the owner's newer frames,
 * and the frames only this window paged would reach every other window.
 */
function framesMirrorPatch(
  prev: FramesState,
  next: FramesState,
): FramesMirrorPatch {
  if (next.generation !== prev.generation) {
    return {
      replace: true,
      framesById: next.framesById,
      frameOrder: next.frameOrder,
    };
  }
  if (next.framesById === prev.framesById) return {};
  const upserted: Record<string, FrameData> = {};
  for (const id of next.frameOrder) {
    const frame = next.framesById[id];
    if (frame && frame !== prev.framesById[id]) upserted[id] = frame;
  }
  const removed =
    next.frameOrder === prev.frameOrder
      ? []
      : prev.frameOrder.filter((id) => !next.framesById[id]);
  return {
    ...(Object.keys(upserted).length > 0 && { upserted }),
    ...(removed.length > 0 && { removed }),
  };
}

export const useFramesStore = create<FramesState>()(
  mirroredStore(
    "frames",
    (set, get) => ({
      framesById: {},
      frameOrder: [],
      generation: 0,

      replaceFrames: (frames) =>
        set({
          framesById: Object.fromEntries(
            frames.map((frame) => [frame.frameId, frame]),
          ),
          frameOrder: frames.map((frame) => frame.frameId),
          generation: get().generation + 1,
        }),

      upsertFrame: (frame) => get().upsertFrames([frame]),

      upsertFrames: (frames) =>
        set((state) => {
          let framesById: Record<string, FrameData> | null = null;
          let frameOrder: string[] | null = null;
          for (const frame of frames) {
            const id = frame.frameId;
            const known = (framesById ?? state.framesById)[id];
            if (known === frame) continue;
            framesById ??= { ...state.framesById };
            framesById[id] = frame;
            if (known) continue;
            frameOrder ??= [...state.frameOrder];
            frameOrder.splice(
              insertionIndex(frameOrder, framesById, frame),
              0,
              id,
            );
          }
          if (!framesById) return state;
          return frameOrder ? { framesById, frameOrder } : { framesById };
        }),

      patchFrame: (id, patch) => get().patchFrames([[id, patch]]),

      patchFrames: (entries) =>
        set((state) => {
          let framesById: Record<string, FrameData> | null = null;
          for (const [id, patch] of entries) {
            const current = (framesById ?? state.framesById)[id];
            if (!current) continue;
            const next = mergeFrameData(current, patch);
            if (next === current) continue;
            framesById ??= { ...state.framesById };
            framesById[id] = next;
          }
          return framesById ? { framesById } : state;
        }),

      removeFrames: (ids) =>
        set((state) => {
          const removed = new Set(
            [...ids].filter((id) => state.framesById[id] !== undefined),
          );
          if (removed.size === 0) return state;
          const framesById = { ...state.framesById };
          for (const id of removed) delete framesById[id];
          return {
            framesById,
            frameOrder: state.frameOrder.filter((id) => !removed.has(id)),
          };
        }),

      clear: (keep) => {
        const { framesById, frameOrder } = get();
        const kept = keep
          ? frameOrder.filter((id) => keep(framesById[id]!))
          : [];
        set({
          framesById: Object.fromEntries(
            kept.map((id) => [id, framesById[id]!]),
          ),
          frameOrder: kept,
          generation: get().generation + 1,
        });
      },
    }),
    { patchOf: framesMirrorPatch },
  ),
);
